<script setup lang="ts">
import { Dialog, DialogPanel, TransitionRoot, TransitionChild } from '@headlessui/vue';
import PopupsSuccessInner from '../.././components/popups/PopupsSuccessInner.vue';
import AddTeamList from '../.././components/common/AddTeamList/AddTeamList.vue';
import Close from '../.././components/icons/CloseIcon.vue';
import { Step, StepType } from '../../../types/step';
import Divider from '@/dashboard/vue/components/common/Divider.vue';
import { POPUPS_ANIMATION } from '../../../constants/popups';
import CreateProjectPopupPosterSide from '@/dashboard/vue/components/popups/CreateProjectPopup/CreateProjectPopupPosterSide.vue';
import CreatePopupTabs from '@/dashboard/vue/components/popups/CreatePopupTabs/CreatePopupTabs.vue';
import CreateProjectPopupDate from '@/dashboard/vue/components/popups/CreateProjectPopup/CreateProjectPopupDate.vue';
import { useCreateProjectPopup } from '@/dashboard/vue/popups/CreateProjectPopup/useCreateProjectPopup';
import PrivacySelector from '@/dashboard/vue/components/popups/PrivacySelector.vue';

const {
  canEditBase,
  step,
  type,
  showPopup,
  project,
  name,
  nameError,
  isCreate,
  buttonText,
  currentUser,
  showPrivate,
  showTeamMenu,
  team,
  tabs,
  onTeamUpdated,
  submitAction,
  closePopup,
  setStep,
} = useCreateProjectPopup();
</script>

<template>
  <TransitionRoot :show="showPopup" as="template">
    <Dialog class="fixed bottom-zero left-zero right-zero top-zero z-[5000]" @close="closePopup">
      <!-- The backdrop, rendered as a fixed sibling to the panel container -->
      <TransitionChild
        as="template"
        :enter="POPUPS_ANIMATION.background.enter"
        :enter-from="POPUPS_ANIMATION.background.enterFrom"
        :enter-to="POPUPS_ANIMATION.background.enterTo"
        :leave="POPUPS_ANIMATION.background.leave"
        :leave-from="POPUPS_ANIMATION.background.leaveFrom"
        :leave-to="POPUPS_ANIMATION.background.leaveTo"
      >
        <div class="inset-0 fixed bottom-zero left-zero right-zero top-zero h-full w-full bg-gray-250/60 dark:bg-black/60" aria-hidden="true" />
      </TransitionChild>

      <!-- Full-screen container to center the panel -->
      <TransitionChild
        as="template"
        :enter="POPUPS_ANIMATION.content.enter"
        :enter-from="POPUPS_ANIMATION.content.enterFrom"
        :enter-to="POPUPS_ANIMATION.content.enterTo"
        :leave="POPUPS_ANIMATION.content.leave"
        :leave-from="POPUPS_ANIMATION.content.leaveFrom"
        :leave-to="POPUPS_ANIMATION.content.leaveTo"
      >
        <div class="absolute h-full w-full overflow-auto p-[47px]">
          <div class="flex min-h-full flex-1 items-center justify-center">
            <!-- The actual dialog panel -->
            <DialogPanel class="flex overflow-hidden rounded-16 bg-white text-center dark:bg-gray-950">
              <div v-if="([Step.Name, Step.Team] as StepType[]).includes(step)" class="flex h-[736px] w-[600px] flex-1">
                <CreateProjectPopupPosterSide v-if="false" />

                <div class="relative flex flex-1">
                  <button class="absolute right-m top-m outline-none" @click="closePopup()">
                    <close class="stroke-black dark:stroke-gray-500" />
                  </button>

                  <div class="flex h-full w-full flex-col items-center">
                    <CreatePopupTabs :current-step="step" :step-changed="setStep" :show-team="showTeamMenu" :tabs="tabs" />

                    <div class="flex w-full flex-1 flex-col items-start">
                      <div v-if="step === Step.Name" class="flex w-full flex-1 flex-col px-[84px]">
                        <div class="flex items-start">
                          <div class="tppr-text-field flex-1" :class="{ error: nameError }">
                            <p class="tppr-text-field__title text-left text-gray-700 dark:text-gray-500">Directory Name</p>

                            <div class="tppr-text-field__field-container">
                              <input v-model="name" type="text" placeholder="New directory" :disabled="!canEditBase" />
                            </div>
                          </div>
                        </div>

                        <CreateProjectPopupDate v-if="project" :project="project" />
                        <Divider class="divider-gray-450 dark:divider-gray-750 my-s mt-m w-full" />
                        <PrivacySelector :type="type" :show-private="showPrivate" :set-type="(newType) => (type = newType)" :can-edit="canEditBase" />

                        <div class="flex-1"></div>
                      </div>

                      <div v-else-if="step === Step.Team" class="flex w-full flex-1 flex-col px-[84px]">
                        <AddTeamList
                          v-if="currentUser"
                          :is-create="isCreate"
                          :on-team-updated="onTeamUpdated"
                          :current-user="currentUser"
                          :team="team"
                          :type="'project'"
                        />
                      </div>

                      <div class="w-full p-xm">
                        <Divider class="divider-gray-450 dark:divider-gray-750 my-s mt-m w-full" />

                        <div class="mt-xm flex w-full justify-between">
                          <button class="button button-ghost h-[48px] max-w-[160px]" @click="() => closePopup()">
                            <span class="button__text">Cancel</span>
                          </button>
                          <button class="button button-primary h-[48px] max-w-[160px]" @click="() => submitAction()">
                            <span class="button__text">{{ buttonText }}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <PopupsSuccessInner v-else-if="step === Step.Success" :action="() => {}" :title="''" />
            </DialogPanel>
          </div>
        </div>
      </TransitionChild>
    </Dialog>
  </TransitionRoot>
</template>
